<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  translationKey: {
    type: String,
    required: true,
  },
  bulkIds: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['deleteSuccess']);

const { t } = useI18n();
const store = useStore();
const bulkDeleteDialogRef = ref(null);
const i18nKey = computed(() => props.translationKey);

const handleBulkDelete = async ids => {
  if (!ids) return;

  try {
    // 根据类型分发到不同的 action
    const idsArray = Array.from(props.bulkIds);

    if (props.type === 'KnowledgeBases') {
      // 删除知识库
      await Promise.all(
        idsArray.map(id => store.dispatch('pilotKnowledgeBases/delete', id))
      );
    } else if (props.type === 'Assistant') {
      // 删除助手
      await Promise.all(
        idsArray.map(id => store.dispatch('pilotAssistants/delete', id))
      );
    } else if (props.type === 'Document') {
      // 删除文档
      await Promise.all(
        idsArray.map(id => store.dispatch('pilotDocuments/delete', id))
      );
    } else if (props.type === 'Faq') {
      // 删除 FAQ
      await Promise.all(
        idsArray.map(id => store.dispatch('pilotFaqs/delete', id))
      );
    } else if (props.type === 'PendingFaqs') {
      // 删除待审核 FAQ
      await Promise.all(
        idsArray.map(id => store.dispatch('pilotPendingFaqs/delete', id))
      );
    } else {
      // 其他类型使用默认的批量删除逻辑
      await store.dispatch('pilotBulkActions/handleBulkDelete', idsArray);
    }

    emit('deleteSuccess');
    useAlert(t(`PILOT.${i18nKey.value}.BULK_DELETE.SUCCESS_MESSAGE`));
  } catch (error) {
    useAlert(t(`PILOT.${i18nKey.value}.BULK_DELETE.ERROR_MESSAGE`));
  }
};

const handleDialogConfirm = async () => {
  await handleBulkDelete(Array.from(props.bulkIds));
  bulkDeleteDialogRef.value?.close();
};

const open = () => {
  bulkDeleteDialogRef.value?.open();
};

defineExpose({ dialogRef: bulkDeleteDialogRef, open });
</script>

<template>
  <Dialog
    ref="bulkDeleteDialogRef"
    type="alert"
    :title="t(`PILOT.${i18nKey}.BULK_DELETE.TITLE`)"
    :description="t(`PILOT.${i18nKey}.BULK_DELETE.DESCRIPTION`)"
    :confirm-button-label="t(`PILOT.${i18nKey}.BULK_DELETE.CONFIRM`)"
    @confirm="handleDialogConfirm"
  />
</template>
